"use client";

import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";

import { cx } from "../../cx";
import styles from "./toggle-group.module.css";

type ToggleGroupProps<T extends string> = Omit<
  ToggleGroupPrimitive.Props<T>,
  "value" | "defaultValue" | "onValueChange" | "multiple" | "className"
> & {
  className?: string;
  value: T;
  onValueChange: (value: T) => void;
  /** `segmented`: square items on a sunken track. `chip`: a row of separate
   *  pills (filter chips), ringed in brand while pressed. */
  variant?: "segmented" | "chip";
};

/** Single-choice toggle row: one item is always pressed. */
function ToggleGroup<T extends string>({
  className,
  value,
  onValueChange,
  variant = "segmented",
  ...props
}: ToggleGroupProps<T>) {
  return (
    <ToggleGroupPrimitive<T>
      data-tui=""
      data-slot="toggle-group"
      data-variant={variant}
      value={[value]}
      onValueChange={(next) => {
        if (next[0]) onValueChange(next[0]);
      }}
      className={cx(styles.group, className)}
      {...props}
    />
  );
}

function ToggleGroupItem<T extends string>({
  className,
  ...props
}: Omit<TogglePrimitive.Props<T>, "className"> & { className?: string }) {
  return (
    <TogglePrimitive<T>
      data-tui=""
      data-slot="toggle-group-item"
      className={cx(styles.item, className)}
      {...props}
    />
  );
}

export { ToggleGroup, ToggleGroupItem };
